import type { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ExternalLink } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

const sourceIdBadgeVariants = cva(
  `inline-flex items-center gap-1 rounded-md font-mono text-xs font-medium transition-colors`,
  {
    variants: {
      variant: {
        default: `border border-border-default/50 bg-bg-accent-muted text-text-accent`,
        compact: `border border-border-default/40 bg-bg-subtle text-text-secondary`,
      },
      clickable: {
        true: `
          cursor-pointer
          hover:border-border-default hover:bg-bg-accent-muted/80
        `,
        false: '',
      },
      size: {
        sm: 'px-1.5 py-0.5 text-[10px]',
        md: 'px-2 py-1 text-xs',
      },
    },
    defaultVariants: {
      variant: 'default',
      clickable: false,
      size: 'md',
    },
  },
);

const iconSizeClasses = {
  sm: 'h-2.5 w-2.5',
  md: 'h-3 w-3',
};

/**
 * True only for `http`/`https` URLs.
 *
 * A `source_url` can originate from untrusted input — a hand-edited Excel
 * cell, for instance — so rendering it directly as an `href` would expose a
 * stored-XSS vector (`javascript:` / `data:` URIs execute on click). This
 * allow-lists web schemes; everything else falls back to a plain, non-linked
 * badge. Typed as a type guard so callers narrow the URL to `string`.
 */
function isSafeExternalUrl(url: string | null | undefined): url is string {
  if (!url || !url.trim()) return false;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

export interface SourceIdBadgeProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'>, VariantProps<typeof sourceIdBadgeVariants> {
  /**
   * The source ID to display (e.g., "PROJ-123", "AB#456")
   */
  sourceId: string;
  /**
   * External URL for the source (e.g. the Jira/ADO browse link). When present
   * and a safe `http(s)` URL, the badge renders as an anchor that opens the
   * issue in a new tab. Unsafe or missing URLs fall back to a plain badge.
   */
  href?: string | null;
  /**
   * Click handler - when provided (and no `href` is rendered), makes the badge
   * a clickable button.
   */
  onClick?: () => void;
  /**
   * Show the external link icon. Always shown when rendered as a link.
   */
  showIcon?: boolean;
}

/**
 * SourceIdBadge - Displays a source_id from external systems (Jira, Azure DevOps).
 *
 * Shows the external identifier for imported deliverables and features. When a
 * safe `href` is supplied it becomes a real anchor that opens the source issue
 * in a new tab; otherwise it renders as plain (optionally button-clickable) text.
 *
 * @example
 * ```tsx
 * // Plain badge
 * <SourceIdBadge sourceId="PROJ-123" />
 *
 * // Clickable external link (opens Jira in a new tab)
 * <SourceIdBadge sourceId="PROJ-123" href="https://acme.atlassian.net/browse/PROJ-123" />
 *
 * // Compact variant for timeline
 * <SourceIdBadge sourceId="PROJ-123" variant="compact" size="sm" />
 * ```
 */
export function SourceIdBadge({
  sourceId,
  href,
  onClick,
  showIcon = false,
  variant,
  size = 'md',
  className,
  ...props
}: SourceIdBadgeProps) {
  const iconSize = iconSizeClasses[size ?? 'md'];

  const content = (
    <>
      <span className="select-all">{sourceId}</span>
      {(showIcon || isSafeExternalUrl(href)) && (
        <ExternalLink className={cn(iconSize, 'opacity-60')} aria-hidden="true" />
      )}
    </>
  );

  // Render as a real anchor only for safe http(s) URLs. The type guard narrows
  // `href` to `string` here, so no non-null assertion is needed.
  if (isSafeExternalUrl(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(sourceIdBadgeVariants({ variant, clickable: true, size }), className)}
        aria-label={`Open ${sourceId} in external system (opens in new tab)`}
      >
        {content}
      </a>
    );
  }

  const clickable = !!onClick;

  return (
    <div
      className={cn(sourceIdBadgeVariants({ variant, clickable, size }), className)}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `Open ${sourceId} in external system` : undefined}
      {...props}
    >
      {content}
    </div>
  );
}
